import React, { forwardRef, useEffect, useState } from "react";
import { useController, useFormContext, useFormState } from "react-hook-form";
import get from "lodash/get";
import bareAxios from "axios";

import { StyledWrapper } from "./filesField.style";

import GenericInputField, {
  TYPES,
} from "../../../../genericInputFields/genericInputField";
import { parseXml } from "../../../../../utils";
import removeCircle from "../../../../../assets/icons/remove_circle.svg";
import useAppContext from "../../../../../hooks/useAppContext";
import useToast from "../../../../../hooks/useToast";

// const FILE_EXTENSION = ['DOC', 'DOCX', 'PDF'];

const FilesField = forwardRef(({ name, settings = {} }, ref) => {
  const displayToast = useToast();
  const { signedUrl, deleteCallbackUrl, uploadedCallbackUrl } = settings;
  const { axios } = useAppContext();
  const { control } = useFormContext();
  const { errors } = useFormState({ name });
  const { field } = useController({
    name,
    control,
  });
  const error = get(errors, name);
  const [files, setFiles] = useState(field.value || []);

  const [currentFileUploadParams, setCurrentFileUploadParams] = useState(null);

  useEffect(() => getSignedFileParams(), []);
  const getSignedFileParams = () => {
    axios.get(signedUrl).then((res) => {
      setCurrentFileUploadParams(res.data);
    });
  };
  const handleFileUpload = (file, callback) => {
    const formData = new FormData();
    formData.append("key", currentFileUploadParams.key);
    formData.append("acl", currentFileUploadParams.acl);
    formData.append("AWSAccessKeyId", currentFileUploadParams.AWSAccessKeyId);
    formData.append("policy", currentFileUploadParams.policy);
    formData.append("signature", currentFileUploadParams.signature);
    formData.append(
      "success_action_status",
      currentFileUploadParams.successActionStatus,
    );
    formData.append(
      "X-Requested-With",
      currentFileUploadParams["x-Requested-With"],
    );
    formData.append("utf8", currentFileUploadParams.utf8);
    formData.append("Content-Type", "");
    formData.append("content-type", file.type);
    formData.append("file", file);

    bareAxios({
      url: currentFileUploadParams.url,
      method: currentFileUploadParams.method,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "*/*",
      },
    }).then((res) => {
      if (callback) {
        callback(res.data);
      }
      getSignedFileParams();
    });
  };

  const handleDeleteFileCallback = (key, callback) => {
    axios
      .post(deleteCallbackUrl, {
        contestfile: {
          key: key,
        },
      })
      .then(() => {
        if (callback) {
          callback();
        }
      })
      .catch(() => {
        displayToast({ success: false, message: "Something went wrong..." });
      });
  };

  const handleAddFileCallback = (key, callback) => {
    axios
      .post(uploadedCallbackUrl, {
        contestfile: {
          key: key,
        },
      })
      .then((res) => {
        if (callback) {
          callback(res.data.data);
        }
      })
      .catch(() => {
        displayToast({ success: false, message: "Something went wrong..." });
      });
  };

  const handleFileChanged = (file) => {
    if (!file) return;
    if (files.map((f) => f.filename).includes(file.name))
      return alert(
        "That file is already uploaded. Please choose another file.",
      );
    handleFileUpload(file, (data) => {
      const xml = parseXml(data);
      const key = xml.getElementsByTagName("Key")[0].textContent;
      handleAddFileCallback(key, (data) => {
        setFiles((prev) => [...prev, { ...data }]);
        displayToast({ success: true, message: "File has been added" });
      });
    });
  };

  useEffect(() => {
    field.onChange(files);
  }, [files]);

  const handleDeleteFile = (key) => {
    handleDeleteFileCallback(key, () => {
      setFiles((prev) => prev.filter((file) => file.key !== key));
      displayToast({ success: true, message: "File has been removed" });
    });
  };

  return (
    <StyledWrapper ref={ref}>
      <GenericInputField
        type={TYPES.FILE}
        control={control}
        name={`${name}.key`}
        required={false}
        error={error}
        handleChange={handleFileChanged}
        accept={undefined}
        bottomMessage="We accept .doc, .docx, .pdf formats up to to 10 megabytes"
      />
      <div>
        {files.map((file, index) => (
          <div key={index}>
            <img
              src={removeCircle}
              alt="remove file"
              onClick={() => handleDeleteFile(file.key)}
            />
            {file.filename}
          </div>
        ))}
      </div>
    </StyledWrapper>
  );
});
FilesField.displayName = "FilesField";

export default FilesField;
